import {
  CALL_OUTCOME_LABELS,
  CALL_OUTCOME_TONES,
  ORDER_STAGE_HINTS,
  ORDER_STAGE_LABELS,
  ORDER_STAGE_TONES,
  STAGE_FLOW_FAILED,
  STAGE_FLOW_MAIN,
  type CallOutcome,
  type OrderStage,
  type StageOverride,
} from "@estelle/domain";
import { CircleHelp, Info } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { STAGE_TONE_CLASS } from "@/modules/orders/stageTones";

/**
 * The order stage badge, the call-outcome label, the overdue tag and the "?"
 * flow (#465): the small things every surface that shows a stage draws.
 *
 * Each says its word in text and carries a dot, so colour is never the only
 * signal. Built on the app's own `Badge` and the tint tokens the other status
 * pills use, each with a dark-theme pair.
 */

function Dot() {
  return (
    <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
  );
}

/** The stage in plain words, with a dot of its tone. */
export function OrderStageBadge({
  stage,
  className,
}: {
  stage: OrderStage;
  className?: string;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 font-medium",
        STAGE_TONE_CLASS[ORDER_STAGE_TONES[stage]],
        className,
      )}
    >
      <Dot />
      {ORDER_STAGE_LABELS[stage]}
    </Badge>
  );
}

/** How the confirmation call went. */
export function ConfirmationLabel({
  outcome,
  className,
}: {
  outcome: CallOutcome;
  className?: string;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 font-medium",
        STAGE_TONE_CLASS[CALL_OUTCOME_TONES[outcome]],
        className,
      )}
    >
      <Dot />
      {CALL_OUTCOME_LABELS[outcome]}
    </Badge>
  );
}

/** "Overdue 19h": a New COD order still unconfirmed past the limit. */
export function OverdueTag({
  hours,
  className,
}: {
  hours: number;
  className?: string;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "border-destructive/40 bg-destructive/10 font-semibold text-destructive",
        className,
      )}
    >
      Overdue {hours}h
    </Badge>
  );
}

const CHEVRON_POINT = 10;
const chevronClip = (first: boolean) =>
  first
    ? `polygon(0 0, calc(100% - ${String(CHEVRON_POINT)}px) 0, 100% 50%, calc(100% - ${String(CHEVRON_POINT)}px) 100%, 0 100%)`
    : `polygon(0 0, calc(100% - ${String(CHEVRON_POINT)}px) 0, 100% 50%, calc(100% - ${String(CHEVRON_POINT)}px) 100%, 0 100%, ${String(CHEVRON_POINT)}px 50%)`;

function Chevron({
  stage,
  state,
  first,
}: {
  stage: OrderStage;
  state: "now" | "done" | "later";
  first: boolean;
}) {
  return (
    <li
      aria-current={state === "now" ? "step" : undefined}
      style={{ clipPath: chevronClip(first) }}
      className={cn(
        "-mr-1.5 py-1.5 pr-3.5 text-xs whitespace-nowrap",
        first ? "pl-3" : "pl-4",
        state === "now" && "bg-primary text-primary-foreground font-semibold",
        state === "done" && "bg-tint-blue text-tint-blue-foreground opacity-80",
        state === "later" && "bg-muted text-muted-foreground",
      )}
    >
      {ORDER_STAGE_LABELS[stage]}
      {state === "now" ? (
        <span className="sr-only"> (current stage)</span>
      ) : null}
    </li>
  );
}

function Row({
  label,
  stages,
  current,
  doneUpTo,
}: {
  label: string;
  stages: readonly OrderStage[];
  current: OrderStage;
  /** Index in `stages` up to which earlier steps read as done; -1 for none. */
  doneUpTo: number;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {label}
      </p>
      <ol className="flex flex-wrap gap-y-1.5">
        {stages.map((stage, index) => (
          <Chevron
            key={stage}
            stage={stage}
            first={index === 0}
            state={
              stage === current ? "now" : index < doneUpTo ? "done" : "later"
            }
          />
        ))}
      </ol>
    </div>
  );
}

/** The journey with the order's current stage lit. */
export function StageFlow({ stage }: { stage: OrderStage }) {
  const onMain = STAGE_FLOW_MAIN.indexOf(stage);
  const onFailed = STAGE_FLOW_FAILED.indexOf(stage);
  const mainDone =
    onMain >= 0
      ? onMain
      : onFailed >= 0
        ? STAGE_FLOW_MAIN.indexOf("out_for_delivery") + 1
        : 0;
  return (
    <div className="flex flex-col gap-3">
      <Row
        label="Normal journey"
        stages={STAGE_FLOW_MAIN}
        current={stage}
        doneUpTo={mainDone}
      />
      <Row
        label="If delivery fails"
        stages={STAGE_FLOW_FAILED}
        current={stage}
        doneUpTo={onFailed}
      />
      <Row
        label="Before shipping"
        stages={["cancelled"]}
        current={stage}
        doneUpTo={0}
      />
      <p className="text-muted-foreground text-xs">
        {ORDER_STAGE_HINTS[stage]}
      </p>
    </div>
  );
}

/** The small "?" beside a stage chip; opens the flow with this stage highlighted. */
export function StageFlowButton({
  stage,
  className,
}: {
  stage: OrderStage;
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Show where ${ORDER_STAGE_LABELS[stage]} sits in the order flow`}
          // Stop the row's own click (it opens the order) from firing.
          onClick={(event) => {
            event.stopPropagation();
          }}
          className={cn(
            "text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex size-5 shrink-0 items-center justify-center rounded-full focus-visible:ring-2 focus-visible:outline-none",
            className,
          )}
        >
          <CircleHelp aria-hidden className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(50rem,calc(100vw-2rem))]"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <p className="mb-3 text-sm font-medium">
          Stage: {ORDER_STAGE_LABELS[stage]}
        </p>
        <StageFlow stage={stage} />
      </PopoverContent>
    </Popover>
  );
}

/** What the "manual" marker says on hover: who set the stage, when, and why. */
function manualStageSummary(override: StageOverride): string {
  const when = new Date(override.setAt).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
  return `Set by hand by ${override.setByName ?? "someone"} on ${when}: ${override.reason}`;
}

/** A small "manual" marker beside a stage a person set by hand (#591). */
export function ManualMarker({
  override,
  className,
}: {
  override: StageOverride;
  className?: string;
}) {
  const summary = manualStageSummary(override);
  return (
    <span
      title={summary}
      aria-label={summary}
      className={cn(
        "text-muted-foreground border-border inline-flex items-center rounded-sm border px-1 text-[0.6875rem] leading-4 font-medium",
        className,
      )}
    >
      manual
    </span>
  );
}

/**
 * The stage badge as the way into its flow (#643): tapping the pill shows
 * where this order sits in the journey. It replaces the 20 px "?" that
 * repeated on every row.
 */
export function StagePill({ stage }: { stage: OrderStage }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${ORDER_STAGE_LABELS[stage]}: show where it sits in the order flow`}
          onClick={(event) => {
            event.stopPropagation();
          }}
          className="focus-visible:ring-ring rounded-md focus-visible:ring-2 focus-visible:outline-none"
        >
          <OrderStageBadge stage={stage} className="cursor-pointer" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(50rem,calc(100vw-2rem))]"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <p className="mb-3 text-sm font-medium">
          Stage: {ORDER_STAGE_LABELS[stage]}
        </p>
        <StageFlow stage={stage} />
      </PopoverContent>
    </Popover>
  );
}

/** The pill, the manual marker and overdue tag when they apply — one unit for any row. */
export function StageCell({
  stage,
  overdueHours,
  override = null,
  className,
}: {
  stage: OrderStage;
  overdueHours: number | null;
  override?: StageOverride | null;
  className?: string;
}) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <StagePill stage={stage} />
      {override ? <ManualMarker override={override} /> : null}
      {overdueHours !== null ? <OverdueTag hours={overdueHours} /> : null}
    </span>
  );
}

/**
 * The whole flow and what each stage means, for the Stage column's heading
 * (#643): the main journey, the failed one, and Cancelled. The meanings are
 * `ORDER_STAGE_HINTS`, the same words the per-order flow and the order page
 * use, so the three never disagree. Choosing a stage shows those orders.
 * Labels only for now; counts wait for the Overview page (#645).
 */
export function StageLegend({
  onPick,
}: {
  onPick?: (stage: OrderStage) => void;
}) {
  const groups: { label: string; stages: readonly OrderStage[] }[] = [
    { label: "Main flow", stages: STAGE_FLOW_MAIN },
    { label: "If delivery fails", stages: STAGE_FLOW_FAILED },
    { label: "Any time", stages: ["cancelled"] },
  ];
  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">
        What each stage means.
        {onPick ? " Choose one to show those orders." : null}
      </p>
      {groups.map((group) => (
        <section key={group.label} className="flex flex-col gap-1">
          <h4 className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
            {group.label}
          </h4>
          <ul className="flex flex-col">
            {group.stages.map((stage) => (
              <li key={stage}>
                <button
                  type="button"
                  disabled={!onPick}
                  onClick={() => {
                    onPick?.(stage);
                  }}
                  className="hover:bg-accent focus-visible:bg-accent grid w-full grid-cols-[9.5rem_1fr] items-start gap-3 rounded-md px-1.5 py-1 text-left outline-none disabled:hover:bg-transparent"
                >
                  <OrderStageBadge stage={stage} className="w-fit" />
                  <span className="text-muted-foreground text-xs leading-5">
                    {ORDER_STAGE_HINTS[stage]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** The info button in the Stage heading that opens the legend (#643). */
export function StageLegendButton({
  onPick,
}: {
  onPick?: (stage: OrderStage) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="What each stage means"
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring ml-1 inline-flex size-5 items-center justify-center rounded-full align-middle focus-visible:ring-2 focus-visible:outline-none"
        >
          <Info aria-hidden className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(34rem,calc(100vw-2rem))]">
        <StageLegend onPick={onPick} />
      </PopoverContent>
    </Popover>
  );
}
